import { AxiosResponse } from 'axios';

interface CacheEntry {
  data: any;
  timestamp: number;
}

const cache = new Map<string, CacheEntry>();
const DEFAULT_TTL = 30000; // 30 seconds

export function memoizeRequest<T extends (...args: any[]) => Promise<AxiosResponse<any>>>(
  fn: T,
  options: { ttl?: number; keyGenerator?: (...args: Parameters<T>) => string } = {}
): T {
  const { ttl = DEFAULT_TTL, keyGenerator = (...args) => JSON.stringify(args) } = options;

  return (async (...args: Parameters<T>) => {
    const key = keyGenerator(...args);
    const cached = cache.get(key);

    // Return cached data if it's still valid
    if (cached && Date.now() - cached.timestamp < ttl) {
      console.log(`[Memoize] Cache hit for: ${key}`);
      return cached.data;
    }

    // Make the actual request
    console.log(`[Memoize] Cache miss for: ${key}`);
    const result = await fn(...args);
    
    // Cache the result
    cache.set(key, {
      data: result,
      timestamp: Date.now(),
    });

    // Clean up old entries
    setTimeout(() => {
      const entry = cache.get(key);
      if (entry && Date.now() - entry.timestamp >= ttl) {
        cache.delete(key);
      }
    }, ttl);

    return result;
  }) as T;
}

export function clearMemoizeCache() {
  cache.clear();
  console.log('[Memoize] Cache cleared');
}